import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { ClientManager } from '../../ElasticAppSearchClient';
import mapRawResults from '../../MapRawResults';
import { ElasticFilters } from '../../../ElasticFilters';
import parsehtml from 'html-react-parser';

/**
 * This is a component that can be used to render WooCommerce-like looking products from Elasticsearch just about anywhere.
 *
 * If you want to render this, check RenderProducts.php class and use the provided hook.
 *
 * @param {Object}                    props
 * @param {{
 *   columns: number,
 *   howMany: number,
 *   filters: Object,
 *   sort: Object,
 *   query: string,
 *   maxForRandomSort: number,
 *   template: string
 * }} props.attributes The attributes passed from the server.
 * @param {{
 *  placeholderImage: string,
 *  woocommerceHooks: string[]
 * }} props.config The configuration object.
 */
const ProductsComponent = ( { attributes, config } ) => {
	const {
		columns,
		howMany,
		filters = {},
		sort,
		query = '',
		maxForRandomSort = 1000,
		template,
		noResultsText,
	} = attributes;

	const {
		placeholderImage,
		woocommerceHooks,
	} = config;

	const [ results, setResults ] = useState( [] );
	const [ didFetch, setDidFetch ] = useState( false );

	const isPlaceholder = ! didFetch;

	// Actual attributes that will be passed to the template.
	const woocommerceArchiveHooks = woocommerceHooks.reduce( ( acc, hook ) => {
		acc[ hook ] = true;
		return acc;
	}, {} );

	const renderAttributes = {
		...attributes,
		woocommerceArchiveHooks,
		listClasses: classNames(
			'products',
			`columns-${ columns }`,
			'qec-wc-products__list',
		),
	};

	/**
	 * Default filters for a query.
	 */
	const defaultFilters = [
		{ blog_id: ( window?.QalaElasticCore?.currentBlogId || 1 ).toString() },
		{ object_type: 'product' },
		{ is_visible: 'true' },
	];

	/**
	 * Default filters for a query.
	 */
	const defaultFiltersNone = [
		{ object_type: 'product_variation' },
	];

	/**
	 * useEffect
	 *
	 * using wp hooks to set query, do query, and set the results.
	 */
	useEffect( () => {
		// There's no random sorting in Elastic App Search so we're cheating a bit by fetching much more
		// than we need and then shuffling the results + taking the first `howMany` results.
		const isRandom = sort === 'random';
		const options = {
			page: {
				size: isRandom ? maxForRandomSort : howMany,
			},
			sort: ! isRandom ? sort : { title: 'asc' },
		};

		const esFilters = new ElasticFilters();
		esFilters.from( filters );

		esFilters.addRequired( {
			all: defaultFilters,
			none: defaultFiltersNone,
		} );

		const fetch = async () => {
			const clientManager = new ClientManager();
			const resultList = await clientManager.search( query, {
				...options,
				filters: esFilters.output(),
			} );

			const mappedResults = mapRawResults( resultList.rawResults );

			// There's no random sorting in Elastic App Search so we're cheating a bit by fetching much more
			// than we need and then shuffling the results + taking the first `howMany` results.
			if ( isRandom ) {
				// shuffle the results
				mappedResults.sort( () => Math.random() - 0.5 );

				// Only take the first `howMany` results
				mappedResults.splice( howMany );
			}
			setResults( mappedResults );
			setDidFetch( true );
		};

		fetch();
	}, [] );

	/**
	 * Get the add to cart button text
	 *
	 * @param {Object} result
	 * @return {string} The add to cart text
	 */
	const getAddToCartText = result => {
		/* eslint-disable camelcase */
		const {
			product_type,
			is_downloadable = false,
			is_purchaseable = true,
		} = result;
		let addToCartText = '';

		const {
			defaultAddToCartText,
			variationAddToCartText,
			subscriptionAddToCartText,
			groupedAddToCartText,
			downloadableAddToCartText,
			notPurchasableAddToCartText,
			externalAddToCartText,
		} = window.QalaElasticCore.translations.woocommerce;

		if ( 'variable' === product_type ) {
			addToCartText = variationAddToCartText;
		} else if ( 'composite' === product_type ) {
			addToCartText = variationAddToCartText;
		} else if ( 'subscription' === product_type ) {
			addToCartText = subscriptionAddToCartText;
		} else if ( 'grouped' === product_type ) {
			addToCartText = groupedAddToCartText;
		} else if ( 'external' === product_type ) {
			addToCartText = externalAddToCartText;
		} else {
			addToCartText = defaultAddToCartText;
		}

		if ( is_downloadable ) {
			addToCartText = downloadableAddToCartText;
		}

		if ( ! is_purchaseable ) {
			addToCartText = notPurchasableAddToCartText;
		}

		/* eslint-enable camelcase */
		return addToCartText;
	};

	/**
	 * Prepare the results for rendering, so we have to do minimal logic inside underscore templates.
	 *
	 * @param {Object} mappedResults Results returned from Elastic App Search
	 * @return {Array} The prepared results
	 */
	const prepareResults = mappedResults => {
		return mappedResults.map( ( result, index ) => {
			const status = result.is_visible ? 'published' : 'private';
			const isFirst = index === 0;
			const isLast = index === results.length - 1;
			const isAjaxAddToCartEnabled = result.product_type === 'simple';
			const addToCartUrl = isAjaxAddToCartEnabled ? `?add-to-cart=${ result.object_id }` : result.url;

			const preparedResults = {
				...result,
				id: result.object_id,
				status,
				isFirst,
				isLast,
				addToCartUrl,
				addToCartText: getAddToCartText( result ),
				isOnSale: result.on_sale === 'yes',
				onSaleText: window.QalaElasticCore.translations.woocommerce.onsale,
				listItemClasses: classNames(
					'qec-wc-products__list-item',
					'product-card',
					'product',
					'type-product',
					`post-${ result.object_id }`,
					`status-${ status }`,
					result.stock_status_raw,
					// We might need some way to check for this. For now we'll just add it.
					'purchasable',
					// We might need some way to check for this. For now we'll just add it.
					'taxable',
					// We might need some way to check for this. For now we'll just add it.
					'shipping-taxable',
					`product-type-${ result.product_type }`,
					isFirst ? 'first' : '',
					isLast ? 'last' : '',
				),
				linkClasses: classNames(
					'woocommerce-LoopProduct-link',
					'woocommerce-loop-product__link',
					'qec-wc-products__link',
					isPlaceholder ? 'qec-wc-products__placeholder' : '',
				),
				buttonClasses: classNames(
					'button',
					`product_type_${ result.product_type }`,
					'add_to_cart_button',
					isAjaxAddToCartEnabled ? 'ajax_add_to_cart' : '',
				),
			};

			return wp.hooks.applyFilters( 'qalaElasticCore.products.prepareResults', preparedResults, result );
		} );
	};

	if ( isPlaceholder ) {
		// Placeholder
		return parsehtml( wp.template( 'qec-results-shimmer-placeholder' )( {
			columns,
			placeholderImage,
			attributes: renderAttributes,
			results: Array.from( { length: howMany } ),
		} ) );
	}

	// What is no results found
	if ( ! results.length ) {
		return parsehtml( wp.template( 'qec-no-results' )( {
			noResultsText,
			attributes: renderAttributes,
		} ) );
	}

	return parsehtml( wp.template( template )( {
		results: prepareResults( results ),
		attributes: renderAttributes,
	} ) );
};

export default ProductsComponent;
